// ✅ src/pages/Asset/AssetTransferApproval.jsx  (FULL FILE - UPDATED WITH localStorage + commonHeaders)
// Flow rules:
// - If approvalStatus is "Approved" -> Edit DISABLED
// - If approvalStatus is "Waiting for Approval" -> Edit ENABLED
// - Approve -> open details page in mode: "APPROVE"
// - Edit    -> open details page in mode: "RECEIVE" (view only)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaChevronDown, FaChevronUp, FaEdit } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/transferListing";
const RECEIVING_ROUTE = "/Asset/AssetTransferReceiving";

// ✅ date format helper
const formatDMY = (dateObj) => {
  if (!dateObj) return "";
  const d = new Date(dateObj);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// yyyy-MM-dd, for the native <input type="date">
const formatISO = (dateObj) => {
  if (!dateObj) return "";
  const d = new Date(dateObj);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const lastNDaysRange = (n = 7) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - n);
  return { from: formatISO(from), to: formatISO(to) };
};
const normalizeStatus = (v) => String(v ?? "").trim().toLowerCase();

const isApprovedStatus = (v) => {
  const st = normalizeStatus(v);
  return st === "approved" || st === "y" || st === "yes" || st === "true";
};

const isWaitingStatus = (v) => {
  const st = normalizeStatus(v);
  return (
    st === "waiting for approval" ||
    st === "waiting" ||
    st === "pending" ||
    st === "pending approval" ||
    st === "not approved" ||
    st === "unapproved"
  );
};

const AssetTransferApproval = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);

  const def = useMemo(() => lastNDaysRange(7), []);
  const defaultFilters = useMemo(
    () => ({
      fromDate: def.from,
      toDate: def.to,
      fromLocation: "",
      toLocation: "",
    }),
    [def]
  );

  const [filters, setFilters] = useState(defaultFilters);

  const [branches, setBranches] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Clear filters back to default range and reload
  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setTimeout(() => fetchList(), 0);
  };

  const getBranchId = (row) =>
    row?.fld_branchid ?? row?.branchid ?? row?.BranchId ?? "";

  const getBranchName = (row) =>
    row?.fld_branchName ??
    row?.branchName ??
    row?.BranchName ??
    String(getBranchId(row) || "");

  const getCol = (row, keys, fallback = "") => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return fallback;
  };

  const loadInit = async () => {
    try {
      setLoadingInit(true);

      const res = await axios.get(`${API_BASE}/init`, {
        params: { loginuserid: userId },
        headers: commonHeaders,
      });

      const data = res?.data?.data || {};
      setBranches(Array.isArray(data?.branches) ? data.branches : []);

      const initFrom = data?.from ? formatISO(data.from) : "";
      const initTo = data?.defaultToDate ? formatISO(data.defaultToDate) : "";

      setFilters((prev) => ({
        ...prev,
        fromDate: initFrom || prev.fromDate,
        toDate: initTo || prev.toDate,
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load init data.");
      setBranches([]);
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchList = async () => {
    try {
      setLoadingList(true);
      setCurrentPage(1);

      const params = {
        pageid: "1",
        fromDate: formatDMY(filters.fromDate),
        toDate: formatDMY(filters.toDate),
        fromLocation: filters.fromLocation,
        toLocation: filters.toLocation,
        loginuserid: userId,
        branchtype: branchType,
        branchid: branchId,
        finYearId,
        finYear,
      };

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: commonHeaders,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load list");
        setRecords([]);
        return;
      }

      setRecords(Array.isArray(res?.data?.data) ? res.data.data : []);
      toast.success("List loaded");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to load approval list"
      );
      setRecords([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.ceil(records.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = records.slice(startIndex, endIndex);

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      pages.push("...");
    }
    return pages;
  };

  const canShowApprove = useMemo(() => {
    return String(branchType || "").toUpperCase() === "HO";
  }, [branchType]);

  const mapRowForReceiving = (row, sno) => {
    const transferId = getCol(
      row,
      ["TransferId", "fld_transferId", "transferId", "Id", "fld_id"],
      ""
    );
    const approvalStatus = getCol(
      row,
      [
        "ApprovalStatus",
        "fld_approvalStatus",
        "Status",
        "fld_status",
        "TransferStatus",
      ],
      ""
    );

    return {
      sno,
      transferId,
      approvalStatus,
           assetType: getCol(
        row,
        ["fld_AssetTypeName", "AssetType", "fld_assetType", "AssetTypeName", "assetType"],
        ""
      ),
      assetSubType: getCol(
        row,
        ["fld_AssetSubTypeName", "AssetSubType", "fld_assetSubType", "AssetSubTypeName", "assetSubType"],
        ""
      ),
      assetIdCodes: getCol(
        row,
        ["fld_idcodeno", "AssetIdCode", "fld_assetIdCode", "idCode", "assetCode"],
        ""
      ),
      fromBranch: getCol(
        row,
        ["FromBranch", "fld_fromBranch", "FromLocation", "fromBranch"],
        ""
      ),
      toBranch: getCol(
        row,
        ["ToBranch", "fld_toBranch", "ToLocation", "toBranch"],
        ""
      ),
      transferDate: getCol(
        row,
        ["fld_TransferDate", "TransferDate", "fld_transferDate", "Date", "fld_date"],
        ""
      ),
      transferReason: getCol(
        row,
        ["fld_TransferReason", "TransferReason", "fld_transferReason", "reason"],
        ""
      ),
    };
  };

  const goToReceiving = (row, sno, mode) => {
    const mapped = mapRowForReceiving(row, sno);

    sessionStorage.setItem("assetTransferSelected", JSON.stringify(mapped));

    navigate(RECEIVING_ROUTE, {
      state: {
        transferId: mapped.transferId,
        transfer: mapped,
        raw: row,
        mode, // ✅ fixed
      },
    });
  };

  const handleApprove = (row, sno) => {
    const flags = row?._flags || {};
    if (flags.canApprove === false) {
      toast.warn("You are not allowed to approve this transfer.");
      return;
    }
    goToReceiving(row, sno, "APPROVE");
  };

  const handleEdit = (row, sno, approvalStatus) => {
    if (!isWaitingStatus(approvalStatus)) {
      toast.info("Edit allowed only for Waiting for Approval.");
      return;
    }
    goToReceiving(row, sno, "RECEIVE");
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TRANSFER APPROVAL</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black border-gray-200"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Transfer Approval
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={fetchList}
                    disabled={loadingList}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loadingList ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    {loadingList ? "Loading..." : "View"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="dd/mm/yyyy"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="dd/mm/yyyy"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    From Location
                  </label>
                  <select
                    value={filters.fromLocation}
                    onChange={(e) => handleFilterChange("fromLocation", e.target.value)}
                    disabled={loadingInit}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    } ${loadingInit ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <option value="">{loadingInit ? "Loading..." : "Select"}</option>
                    {branches.map((b, idx) => (
                      <option key={`${getBranchId(b)}-${idx}`} value={getBranchId(b)}>
                        {getBranchName(b)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    To Location
                  </label>
                  <select
                    value={filters.toLocation}
                    onChange={(e) => handleFilterChange("toLocation", e.target.value)}
                    disabled={loadingInit}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    } ${loadingInit ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <option value="">{loadingInit ? "Loading..." : "Select"}</option>
                    {branches.map((b, idx) => (
                      <option key={`${getBranchId(b)}-${idx}`} value={getBranchId(b)}>
                        {getBranchName(b)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {records.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div
                  className={`text-lg font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  No Records Found
                </div>
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No asset transfer records match the current filter criteria.
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Asset Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Asset SubType</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Asset IdCodes</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">From Branch</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">To Branch</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Transfer Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Transfer Reason</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Approval Status</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Action</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentRecords.map((r, idx) => {
                    const sno = startIndex + idx + 1;

                    const approvalStatus = getCol(r, [
                      "ApprovalStatus",
                      "fld_approvalStatus",
                      "Status",
                      "fld_status",
                      "TransferStatus",
                    ]);
                                        const assetType = getCol(r, [
                      "fld_AssetTypeName",
                      "AssetType",
                      "fld_assetType",
                      "AssetTypeName",
                      "assetType",
                    ]);
                    const assetSubType = getCol(r, [
                      "fld_AssetSubTypeName",
                      "AssetSubType",
                      "fld_assetSubType",
                      "AssetSubTypeName",
                      "assetSubType",
                    ]);
                    const assetIdCodes = getCol(r, [
                      "fld_idcodeno",
                      "AssetIdCode",
                      "fld_assetIdCode",
                      "idCode",
                      "assetCode",
                    ]);
                    const fromBranch = getCol(r, [
                      "FromBranch",
                      "fld_fromBranch",
                      "FromLocation",
                      "fromBranch",
                    ]);
                    const toBranch = getCol(r, [
                      "ToBranch",
                      "fld_toBranch",
                      "ToLocation",
                      "toBranch",
                    ]);
                    const transferDate = getCol(r, [
                      "fld_TransferDate",
                      "TransferDate",
                      "fld_transferDate",
                      "Date",
                      "fld_date",
                    ]);
                    const transferReason = getCol(r, [
                      "fld_TransferReason",
                      "TransferReason",
                      "fld_transferReason",
                      "reason",
                    ]);

                    const flags = r?._flags || {};
                    const uiMode = r?._uiMode || {};

                    const allowApprove =
                      (uiMode.showApprove ?? canShowApprove) &&
                      (flags.canApprove ?? true);

                    const allowEditByStatus = isWaitingStatus(approvalStatus);
                    const allowEdit = (flags.canEdit ?? true) && allowEditByStatus;

                    return (
                      <tr
                        key={r?.id ?? r?.TransferId ?? `${sno}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {sno}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {String(assetType || "")}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {String(assetSubType || "")}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {String(assetIdCodes || "")}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {String(fromBranch || "")}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {String(toBranch || "")}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {String(transferDate || "")}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {String(transferReason || "")}
                        </td>
                        <td className="px-6 py-4 text-center text-sm">
                          {String(approvalStatus || "")}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => handleEdit(r, sno, approvalStatus)}
                              disabled={!allowEdit}
                              className={`text-lg transition ${
                                allowEdit
                                  ? "text-yellow-500 hover:text-yellow-600"
                                  : "opacity-40 cursor-not-allowed text-yellow-500"
                              }`}
                              title={allowEdit ? "Edit" : "Edit disabled (Approved)"}
                            >
                              <FaEdit />
                            </button>

                            {allowApprove ? (
                              <button
                                onClick={() => handleApprove(r, sno)}
                                disabled={flags.canApprove === false}
                                className={`px-3 py-1 rounded text-sm font-semibold ${buttonClass} ${hoverClass} text-white ${
                                  flags.canApprove === false ? "opacity-60 cursor-not-allowed" : ""
                                }`}
                                title="Approve"
                              >
                                Approve
                              </button>
                            ) : (
                              <span className={`${isDark ? "text-gray-400" : "text-gray-500"} text-sm`}>
                                -
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {records.length > 0 && (
            <div className="flex justify-start mt-6">
              <div className="flex gap-1">
                {renderPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => page !== "..." && setCurrentPage(page)}
                    disabled={page === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : page === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTransferApproval;